import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Plus,
  Package,
  MapPin,
  User,
  Clock,
  ChevronDown,
  X,
  Check,
  Filter,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  UserPlus,
  RotateCcw,
  History,
  FileText,
  ExternalLink,
  Calendar,
  Settings,
  Download,
} from 'lucide-react';
import { CanEdit, ReadOnlyBadge } from '@components/common/RequirePermission';
import { useModuleContext } from '@utils/moduleAccess';
import { useAuthStore } from '@store/authStore';
import { fetchWithTimeout, buildApiUrl } from '@utils/api';
import { getAccessToken } from '@lib/msal';

// Types matching the API
type AssetStatus = 'available' | 'assigned' | 'in_use' | 'in_transit' | 'maintenance' | 'lost' | 'retired';
type AssetCategory = 'display' | 'equipment' | 'furniture' | 'signage' | 'av_equipment' | 'other';
type AssetCondition = 'excellent' | 'good' | 'fair' | 'poor';

interface Asset {
  id: string;
  name: string;
  category: AssetCategory;
  description?: string;
  serialNumber?: string;
  assetTag?: string;
  status: AssetStatus;
  currentLocation?: string;
  storageLocation?: string;
  assignedToId?: string;
  assignedToName?: string;
  assignedToEmail?: string;
  assignedDate?: string;
  eventId?: string;
  eventName?: string;
  eventDate?: string;
  notes?: string;
  purchaseDate?: string;
  purchaseCost?: number;
  condition?: AssetCondition;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

interface AssetHistoryEntry {
  id: string;
  assetId: string;
  action: 'created' | 'assigned' | 'returned' | 'moved' | 'status_changed' | 'updated';
  timestamp: string;
  performedBy: string;
  previousStatus?: AssetStatus;
  newStatus?: AssetStatus;
  previousAssignedTo?: string;
  newAssignedTo?: string;
  previousLocation?: string;
  newLocation?: string;
  notes?: string;
}

interface AssetWithHistory extends Asset {
  history: AssetHistoryEntry[];
}

interface BeaconPerson {
  id: number;
  name: string;
  email: string | null;
}

interface BeaconEvent {
  id: number;
  name: string;
  startDate: string | null;
  endDate: string | null;
  location: string | null;
}

interface AssetStats {
  total: number;
  byStatus: Record<AssetStatus, number>;
  byCategory: Record<string, number>;
}

interface CustomCategory {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

// Labels and styles
const statusLabels: Record<AssetStatus, string> = {
  available: 'Available',
  assigned: 'Assigned',
  in_use: 'In Use',
  in_transit: 'In Transit',
  maintenance: 'Maintenance',
  lost: 'Lost',
  retired: 'Retired',
};

const statusStyles: Record<AssetStatus, string> = {
  available: 'bg-green-100 text-green-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_use: 'bg-purple-100 text-purple-700',
  in_transit: 'bg-yellow-100 text-yellow-700',
  maintenance: 'bg-orange-100 text-orange-700',
  lost: 'bg-red-100 text-red-700',
  retired: 'bg-gray-100 text-gray-500',
};

// Default category labels (used when categories haven't loaded yet)
const defaultCategoryLabels: Record<string, string> = {
  display: 'Display (Banners, Stands)',
  equipment: 'Equipment (POS, Tablets)',
  furniture: 'Furniture (Tables, Chairs)',
  signage: 'Signage (Signs, Boards)',
  av_equipment: 'AV Equipment',
  other: 'Other',
};

const conditionLabels: Record<AssetCondition, string> = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

const actionLabels: Record<string, string> = {
  created: 'Created',
  assigned: 'Assigned',
  returned: 'Returned',
  moved: 'Moved',
  status_changed: 'Status Changed',
  updated: 'Updated',
};

// Initial form state
const initialFormState: Partial<Asset> = {
  name: '',
  category: 'display',
  description: '',
  serialNumber: '',
  assetTag: '',
  status: 'available',
  currentLocation: '',
  storageLocation: '',
  notes: '',
  condition: 'good',
};

export default function Assets() {
  const { user, teamRole } = useAuthStore();
  const moduleAccess = useModuleContext();
  // canEdit is false when module access is 'read' (even if global role is Admin/Regular)
  const canEdit = moduleAccess === 'edit' && (teamRole === 'Admin' || teamRole === 'Regular');

  // List state
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<AssetStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<AssetStatus | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetWithHistory | null>(null);
  const [formData, setFormData] = useState<Partial<Asset>>(initialFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Person search state (for assignment)
  const [personSearchTerm, setPersonSearchTerm] = useState('');
  const [personResults, setPersonResults] = useState<BeaconPerson[]>([]);
  const [isSearchingPeople, setIsSearchingPeople] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<BeaconPerson | null>(null);
  const [beaconAccountId, setBeaconAccountId] = useState<string | null>(null);

  // Event search state (for linking to Beacon events)
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [eventResults, setEventResults] = useState<BeaconEvent[]>([]);
  const [isSearchingEvents, setIsSearchingEvents] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BeaconEvent | null>(null);

  // Category management state
  const [categories, setCategories] = useState<CustomCategory[]>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategories, setEditingCategories] = useState<CustomCategory[]>([]);
  const [isSavingCategories, setIsSavingCategories] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const filterRef = useRef<HTMLDivElement>(null);

  // Helper to get category name from id
  const getCategoryName = useCallback((id: string): string => {
    const cat = categories.find(c => c.id === id);
    if (cat) return cat.name;
    return defaultCategoryLabels[id] || id;
  }, [categories]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close filters when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedStatus) params.set('status', selectedStatus);
      if (selectedCategory) params.set('category', selectedCategory);

      const token = await getAccessToken();
      const url = buildApiUrl(`/assets?${params}`);
      const { data, error: fetchError } = await fetchWithTimeout<{
        success: boolean;
        data: Asset[];
        pagination: { total: number };
      }>(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : teamRole === 'Regular' ? 'staff' : 'readonly',
        },
      });

      if (fetchError) {
        setError(fetchError);
        return;
      }

      if (data?.success) {
        setAssets(data.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch assets');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, selectedStatus, selectedCategory, user?.email, teamRole]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const url = buildApiUrl('/assets/stats');
      const { data } = await fetchWithTimeout<{ success: boolean; data: AssetStats }>(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : teamRole === 'Regular' ? 'staff' : 'readonly',
        },
      });

      if (data?.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [user?.email, teamRole]);

  // Fetch asset with history
  const fetchAssetDetail = async (id: string) => {
    try {
      const token = await getAccessToken();
      const url = buildApiUrl(`/assets/${id}`);
      const { data, error: fetchError } = await fetchWithTimeout<{
        success: boolean;
        data: AssetWithHistory;
      }>(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : teamRole === 'Regular' ? 'staff' : 'readonly',
        },
      });

      if (fetchError) {
        setError(fetchError);
        return null;
      }

      return data?.data || null;
    } catch (err) {
      console.error('Failed to fetch asset detail:', err);
      return null;
    }
  };

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const response = await fetch(`${apiBase}/api/settings/asset-categories`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        if (data.data?.categories) {
          setCategories(data.data.categories);
        }
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      // Fall back to defaults
      setCategories([
        { id: 'display', name: 'Display (Banners, Stands)', isDefault: true },
        { id: 'equipment', name: 'Equipment (POS, Tablets)', isDefault: true },
        { id: 'furniture', name: 'Furniture (Tables, Chairs)', isDefault: true },
        { id: 'signage', name: 'Signage (Signs, Boards)', isDefault: true },
        { id: 'av_equipment', name: 'AV Equipment', isDefault: true },
        { id: 'other', name: 'Other', isDefault: true },
      ]);
    }
  }, []);

  // Save categories
  const saveCategories = async () => {
    setIsSavingCategories(true);
    try {
      const token = await getAccessToken();
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const response = await fetch(`${apiBase}/api/settings/asset-categories`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
        },
        body: JSON.stringify({ categories: editingCategories }),
      });

      if (response.ok) {
        setCategories(editingCategories);
        setShowCategoryModal(false);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save categories');
      }
    } catch (_err) {
      setError('Failed to save categories');
    } finally {
      setIsSavingCategories(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAssets();
    fetchStats();
    fetchCategories();
  }, [fetchAssets, fetchStats, fetchCategories]);

  // Search people in Beacon
  const searchPeople = async (query: string) => {
    if (query.length < 3) {
      setPersonResults([]);
      return;
    }

    setIsSearchingPeople(true);
    try {
      const token = await getAccessToken();
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const params = new URLSearchParams({ search: query, limit: '10' });
      const response = await fetch(`${apiBase}/api/beacon/people?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json();
        setPersonResults(data.data || []);
        if (data.beaconAccountId) {
          setBeaconAccountId(data.beaconAccountId);
        }
      }
    } catch (err) {
      console.error('Failed to search people:', err);
    } finally {
      setIsSearchingPeople(false);
    }
  };

  // Debounced person search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchPeople(personSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [personSearchTerm]);

  // Search events in Beacon
  const searchEvents = async (query: string) => {
    if (query.length < 2) {
      setEventResults([]);
      return;
    }

    setIsSearchingEvents(true);
    try {
      const token = await getAccessToken();
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const params = new URLSearchParams({ search: query, limit: '10' });
      const response = await fetch(`${apiBase}/api/beacon/events?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json();
        setEventResults(data.data || []);
        if (data.beaconAccountId && !beaconAccountId) {
          setBeaconAccountId(data.beaconAccountId);
        }
      }
    } catch (err) {
      console.error('Failed to search events:', err);
    } finally {
      setIsSearchingEvents(false);
    }
  };

  // Debounced event search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchEvents(eventSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [eventSearchTerm]);

  // Create or update asset
  const handleSaveAsset = async () => {
    if (!formData.name || !formData.category) {
      setError('Name and category are required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const isEdit = !!selectedAsset;
      const url = isEdit
        ? buildApiUrl(`/assets/${selectedAsset.id}`)
        : buildApiUrl('/assets');

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-name': user?.name || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : teamRole === 'Regular' ? 'staff' : 'readonly',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save asset');
      }

      setShowAddModal(false);
      setShowDetailModal(false);
      setSelectedAsset(null);
      setFormData(initialFormState);
      setSelectedEvent(null);
      setEventSearchTerm('');
      setEventResults([]);
      fetchAssets();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save asset');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete asset
  const handleDeleteAsset = async () => {
    if (!selectedAsset) return;

    if (!confirm(`Are you sure you want to delete "${selectedAsset.name}"? This cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const url = buildApiUrl(`/assets/${selectedAsset.id}`);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : 'staff',
        },
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete asset');
      }

      setShowDetailModal(false);
      setSelectedAsset(null);
      fetchAssets();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete asset');
    } finally {
      setIsDeleting(false);
    }
  };

  // Assign asset to person
  const handleAssignAsset = async () => {
    if (!selectedAsset || !selectedPerson) return;

    setIsSaving(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const url = buildApiUrl(`/assets/${selectedAsset.id}/assign`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-name': user?.name || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : 'staff',
        },
        body: JSON.stringify({
          assignedToId: selectedPerson.id.toString(),
          assignedToName: selectedPerson.name,
          assignedToEmail: selectedPerson.email || '',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to assign asset');
      }

      setShowAssignModal(false);
      setSelectedPerson(null);
      setPersonSearchTerm('');
      setPersonResults([]);
      fetchAssets();
      fetchStats();

      // Refresh detail if open
      if (showDetailModal && selectedAsset) {
        const updated = await fetchAssetDetail(selectedAsset.id);
        if (updated) setSelectedAsset(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign asset');
    } finally {
      setIsSaving(false);
    }
  };

  // Return asset
  const handleReturnAsset = async (notes?: string) => {
    if (!selectedAsset) return;

    setIsSaving(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const url = buildApiUrl(`/assets/${selectedAsset.id}/return`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-user-email': user?.email || '',
          'x-user-name': user?.name || '',
          'x-user-role': teamRole === 'Admin' ? 'admin' : 'staff',
        },
        body: JSON.stringify({ notes }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to return asset');
      }

      fetchAssets();
      fetchStats();

      // Refresh detail if open
      if (showDetailModal && selectedAsset) {
        const updated = await fetchAssetDetail(selectedAsset.id);
        if (updated) setSelectedAsset(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return asset');
    } finally {
      setIsSaving(false);
    }
  };

  // Open asset detail
  const openAssetDetail = async (asset: Asset) => {
    const detail = await fetchAssetDetail(asset.id);
    if (detail) {
      setSelectedAsset(detail);
      setShowDetailModal(true);
    }
  };

  // Open edit modal
  const openEditModal = () => {
    if (selectedAsset) {
      setFormData({
        name: selectedAsset.name,
        category: selectedAsset.category,
        description: selectedAsset.description || '',
        serialNumber: selectedAsset.serialNumber || '',
        assetTag: selectedAsset.assetTag || '',
        status: selectedAsset.status,
        currentLocation: selectedAsset.currentLocation || '',
        storageLocation: selectedAsset.storageLocation || '',
        notes: selectedAsset.notes || '',
        condition: selectedAsset.condition || 'good',
        purchaseDate: selectedAsset.purchaseDate || '',
        purchaseCost: selectedAsset.purchaseCost,
        eventId: selectedAsset.eventId,
        eventName: selectedAsset.eventName,
        eventDate: selectedAsset.eventDate,
      });
      // Set selected event if there is one
      if (selectedAsset.eventId) {
        setSelectedEvent({
          id: parseInt(selectedAsset.eventId),
          name: selectedAsset.eventName || '',
          startDate: selectedAsset.eventDate || null,
          endDate: null,
          location: null,
        });
      } else {
        setSelectedEvent(null);
      }
      setEventSearchTerm('');
      setEventResults([]);
      setShowDetailModal(false);
      setShowAddModal(true);
    }
  };

  // Format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Export assets to CSV
  const handleExport = () => {
    if (assets.length === 0) return;

    const headers = ['Name', 'Asset Tag', 'Serial Number', 'Category', 'Status', 'Condition', 'Current Location', 'Storage Location', 'Assigned To', 'Assigned Email', 'Assigned Date', 'Event', 'Description', 'Notes', 'Last Updated'];
    const rows = assets.map((asset) => [
      asset.name,
      asset.assetTag || '',
      asset.serialNumber || '',
      getCategoryName(asset.category),
      statusLabels[asset.status],
      asset.condition ? conditionLabels[asset.condition] : '',
      asset.currentLocation || '',
      asset.storageLocation || '',
      asset.assignedToName || '',
      asset.assignedToEmail || '',
      asset.assignedDate ? formatDate(asset.assignedDate) : '',
      asset.eventName || '',
      asset.description || '',
      asset.notes || '',
      formatDate(asset.updatedAt),
    ]);

    // Build CSV with proper escaping
    const escapeCsv = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map(row => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sca-uk-assets-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Count active filters
  const activeFilterCount = [selectedStatus, selectedCategory].filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {(['available', 'assigned', 'in_use', 'in_transit', 'maintenance', 'lost', 'retired'] as AssetStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setSelectedStatus(selectedStatus === status ? '' : status)}
              className={`card text-center p-4 transition-all ${
                selectedStatus === status
                  ? 'ring-2 ring-sca-purple-500 bg-sca-purple-50'
                  : 'hover:shadow-md'
              }`}
            >
              <p className="text-2xl font-semibold text-gray-900">
                {stats.byStatus[status] || 0}
              </p>
              <p className="text-xs text-gray-500">{statusLabels[status]}</p>
            </button>
          ))}
        </div>
      )}

      {/* Header actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-3 items-start">
          {/* Search input */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                placeholder="Search by name, serial number, or tag..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
              )}
            </div>
          </div>

          {/* Filter dropdown */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors ${
                activeFilterCount > 0 ? 'border-sca-purple-500 bg-sca-purple-50' : 'border-gray-300'
              }`}
            >
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>

            {showFilters && (
              <div className="absolute z-10 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value as AssetCategory | '')}
                    className="input"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as AssetStatus | '')}
                    className="input"
                  >
                    <option value="">All Statuses</option>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setSelectedStatus('');
                      setSelectedCategory('');
                    }}
                    className="text-sm text-sca-purple-600 hover:text-sca-purple-700"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 items-center">
          <ReadOnlyBadge />
          {assets.length > 0 && (
            <button
              onClick={handleExport}
              className="btn-secondary"
              title="Export assets to CSV"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          )}
          <CanEdit>
            <button
              onClick={() => {
                setFormData(initialFormState);
                setSelectedAsset(null);
                setShowAddModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Asset
            </button>
          </CanEdit>
        </div>
      </div>

      {/* Active filter chips */}
      {(selectedStatus || selectedCategory) && (
        <div className="flex flex-wrap gap-2">
          {selectedStatus && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sca-purple-100 text-sca-purple-700 text-xs font-medium rounded-full">
              {statusLabels[selectedStatus]}
              <button
                onClick={() => setSelectedStatus('')}
                className="hover:bg-sca-purple-200 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {selectedCategory && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sca-purple-100 text-sca-purple-700 text-xs font-medium rounded-full">
              {getCategoryName(selectedCategory)}
              <button
                onClick={() => setSelectedCategory('')}
                className="hover:bg-sca-purple-200 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Assets table */}
      {!isLoading && assets.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location / Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assets.map((asset) => (
                  <tr
                    key={asset.id}
                    onClick={() => openAssetDetail(asset)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Package className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{asset.name}</div>
                          {(asset.serialNumber || asset.assetTag) && (
                            <div className="text-xs text-gray-500">
                              {asset.assetTag && <span>#{asset.assetTag}</span>}
                              {asset.assetTag && asset.serialNumber && <span> · </span>}
                              {asset.serialNumber && <span>S/N: {asset.serialNumber}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getCategoryName(asset.category).split(' ')[0]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${statusStyles[asset.status]}`}>
                        {statusLabels[asset.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {asset.assignedToName ? (
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {asset.assignedToName}
                        </span>
                      ) : asset.currentLocation ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {asset.currentLocation}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {asset.assignedDate ? (
                        <span title="Assigned date">{formatDate(asset.assignedDate)}</span>
                      ) : (
                        <span title="Last updated">{formatDate(asset.updatedAt)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && assets.length === 0 && (
        <div className="text-center py-16 card">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {debouncedSearch || selectedStatus || selectedCategory
              ? 'No assets found'
              : 'No assets yet'}
          </h3>
          <p className="text-gray-500 max-w-sm mx-auto mb-4">
            {debouncedSearch || selectedStatus || selectedCategory
              ? 'Try adjusting your search or filters.'
              : 'Add your first asset to start tracking equipment, banners, and other items.'}
          </p>
          {canEdit && !debouncedSearch && !selectedStatus && !selectedCategory && (
            <button
              onClick={() => {
                setFormData(initialFormState);
                setSelectedAsset(null);
                setShowAddModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Asset
            </button>
          )}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-16">
          <Loader2 className="h-8 w-8 text-sca-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading assets...</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {selectedAsset ? 'Edit Asset' : 'Add New Asset'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedAsset(null);
                  setFormData(initialFormState);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="e.g., Pull-up Banner #1"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Category <span className="text-red-500">*</span>
                    </label>
                    {teamRole === 'Admin' && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCategories([...categories]);
                          setShowCategoryModal(true);
                        }}
                        className="text-xs text-sca-purple-600 hover:text-sca-purple-700 flex items-center gap-1"
                      >
                        <Settings className="h-3 w-3" />
                        Manage
                      </button>
                    )}
                  </div>
                  <select
                    value={formData.category || 'display'}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as AssetCategory })}
                    className="input"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status || 'available'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as AssetStatus })}
                    className="input"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asset Tag</label>
                  <input
                    type="text"
                    value={formData.assetTag || ''}
                    onChange={(e) => setFormData({ ...formData, assetTag: e.target.value })}
                    className="input"
                    placeholder="e.g., SCA-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                  <input
                    type="text"
                    value={formData.serialNumber || ''}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    className="input"
                    placeholder="Manufacturer serial number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <select
                    value={formData.condition || 'good'}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value as AssetCondition })}
                    className="input"
                  >
                    {Object.entries(conditionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Location</label>
                  <input
                    type="text"
                    value={formData.currentLocation || ''}
                    onChange={(e) => setFormData({ ...formData, currentLocation: e.target.value })}
                    className="input"
                    placeholder="Where is it now?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Storage Location</label>
                  <input
                    type="text"
                    value={formData.storageLocation || ''}
                    onChange={(e) => setFormData({ ...formData, storageLocation: e.target.value })}
                    className="input"
                    placeholder="Default storage location"
                  />
                </div>
              </div>

              {/* Event (linked to Beacon) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event <span className="text-xs text-gray-400 font-normal">(from Beacon)</span>
                </label>
                {selectedEvent ? (
                  <div className="flex items-center gap-2 p-3 bg-sca-purple-50 border border-sca-purple-200 rounded-lg">
                    <Calendar className="h-4 w-4 text-sca-purple-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{selectedEvent.name}</p>
                      {selectedEvent.startDate && (
                        <p className="text-xs text-gray-500">{formatDate(selectedEvent.startDate)}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedEvent(null);
                        setFormData({
                          ...formData,
                          eventId: undefined,
                          eventName: undefined,
                          eventDate: undefined,
                        });
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={eventSearchTerm}
                      onChange={(e) => setEventSearchTerm(e.target.value)}
                      className="input pl-10"
                      placeholder="Search events (min 2 characters)..."
                    />
                    {isSearchingEvents && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                    )}
                  </div>
                )}
                {eventResults.length > 0 && !selectedEvent && (
                  <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                    {eventResults.map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => {
                          setSelectedEvent(event);
                          setFormData({
                            ...formData,
                            eventId: event.id.toString(),
                            eventName: event.name,
                            eventDate: event.startDate || undefined,
                          });
                          setEventSearchTerm('');
                          setEventResults([]);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{event.name}</p>
                          {event.startDate && (
                            <p className="text-xs text-gray-500">{formatDate(event.startDate)}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  rows={2}
                  placeholder="Brief description of the asset..."
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Any additional notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedAsset(null);
                    setFormData(initialFormState);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAsset}
                  disabled={isSaving || !formData.name}
                  className="btn-primary"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {selectedAsset ? 'Save Changes' : 'Create Asset'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Fixed header */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div>
                <h2 className="text-lg font-semibold">{selectedAsset.name}</h2>
                <p className="text-sm text-gray-500">
                  {selectedAsset.assetTag && `#${selectedAsset.assetTag}`}
                  {selectedAsset.assetTag && selectedAsset.serialNumber && ' · '}
                  {selectedAsset.serialNumber && `S/N: ${selectedAsset.serialNumber}`}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedAsset(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Status and quick actions */}
              <div className="flex flex-wrap items-center gap-3">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusStyles[selectedAsset.status]}`}>
                  {statusLabels[selectedAsset.status]}
                </span>
                <span className="text-sm text-gray-500">
                  {getCategoryName(selectedAsset.category)}
                </span>
                {selectedAsset.condition && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selectedAsset.condition === 'excellent' ? 'bg-green-100 text-green-700' :
                    selectedAsset.condition === 'good' ? 'bg-blue-100 text-blue-700' :
                    selectedAsset.condition === 'fair' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {conditionLabels[selectedAsset.condition]}
                  </span>
                )}
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {selectedAsset.assignedToName && (
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Assigned To</p>
                        <p className="text-sm font-medium">{selectedAsset.assignedToName}</p>
                        {selectedAsset.assignedToEmail && (
                          <a
                            href={`mailto:${selectedAsset.assignedToEmail}`}
                            className="text-xs text-sca-purple-600 hover:underline"
                          >
                            {selectedAsset.assignedToEmail}
                          </a>
                        )}
                        {selectedAsset.assignedDate && (
                          <p className="text-xs text-gray-500">Since {formatDate(selectedAsset.assignedDate)}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedAsset.currentLocation && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Current Location</p>
                        <p className="text-sm font-medium">{selectedAsset.currentLocation}</p>
                      </div>
                    </div>
                  )}

                  {selectedAsset.storageLocation && (
                    <div className="flex items-start gap-3">
                      <Package className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Storage Location</p>
                        <p className="text-sm font-medium">{selectedAsset.storageLocation}</p>
                      </div>
                    </div>
                  )}

                  {selectedAsset.eventName && (
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Event</p>
                        <p className="text-sm font-medium">{selectedAsset.eventName}</p>
                        {selectedAsset.eventDate && (
                          <p className="text-xs text-gray-500">{formatDate(selectedAsset.eventDate)}</p>
                        )}
                        {selectedAsset.eventId && beaconAccountId && (
                          <a
                            href={`https://app.beaconcrm.org/account/${beaconAccountId}/record/event/${selectedAsset.eventId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-sca-purple-600 hover:underline inline-flex items-center gap-1"
                          >
                            View in Beacon <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Last Updated</p>
                      <p className="text-sm">{formatDateTime(selectedAsset.updatedAt)}</p>
                      <p className="text-xs text-gray-500">by {selectedAsset.updatedBy}</p>
                    </div>
                  </div>

                  {selectedAsset.description && (
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Description</p>
                        <p className="text-sm">{selectedAsset.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedAsset.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Notes</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedAsset.notes}</p>
                </div>
              )}

              {/* History */}
              {selectedAsset.history && selectedAsset.history.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    History
                  </h3>
                  <div className="space-y-3">
                    {selectedAsset.history.slice(0, 10).map((entry) => (
                      <div key={entry.id} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-sca-purple-500 mt-1.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900">
                            <span className="font-medium">{actionLabels[entry.action] || entry.action}</span>
                            {entry.newAssignedTo && ` to ${entry.newAssignedTo}`}
                            {entry.newStatus && entry.previousStatus && ` from ${statusLabels[entry.previousStatus]} to ${statusLabels[entry.newStatus]}`}
                            {entry.newLocation && ` to ${entry.newLocation}`}
                          </p>
                          {entry.notes && <p className="text-gray-500">{entry.notes}</p>}
                          <p className="text-xs text-gray-400">
                            {formatDateTime(entry.timestamp)} · {entry.performedBy}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Fixed footer with actions */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl">
              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex gap-2">
                  {canEdit && teamRole === 'Admin' && (
                    <button
                      onClick={handleDeleteAsset}
                      disabled={isDeleting}
                      className="btn-danger"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="flex gap-2">
                  {canEdit && selectedAsset.status === 'assigned' && (
                    <button
                      onClick={() => handleReturnAsset()}
                      disabled={isSaving}
                      className="btn-secondary"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Return
                    </button>
                  )}

                  {canEdit && selectedAsset.status === 'available' && (
                    <button
                      onClick={() => {
                        setShowAssignModal(true);
                        setPersonSearchTerm('');
                        setPersonResults([]);
                        setSelectedPerson(null);
                      }}
                      className="btn-secondary"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Assign
                    </button>
                  )}

                  {canEdit && (
                    <button onClick={openEditModal} className="btn-primary">
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  )}

                  {selectedAsset.assignedToId && beaconAccountId && (
                    <a
                      href={`https://app.beaconcrm.org/account/${beaconAccountId}/record/person/${selectedAsset.assignedToId}/timeline`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View in Beacon
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Assign Asset</h2>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedPerson(null);
                  setPersonSearchTerm('');
                  setPersonResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Assign <span className="font-medium">{selectedAsset.name}</span> to a person from Beacon CRM.
              </p>

              {/* Person search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Person
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={personSearchTerm}
                    onChange={(e) => setPersonSearchTerm(e.target.value)}
                    className="input pl-10"
                    placeholder="Search by name (min 3 characters)..."
                  />
                  {isSearchingPeople && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                  )}
                </div>
              </div>

              {/* Search results */}
              {personResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {personResults.map((person) => (
                    <button
                      key={person.id}
                      onClick={() => setSelectedPerson(person)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between ${
                        selectedPerson?.id === person.id ? 'bg-sca-purple-50' : ''
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{person.name}</p>
                        {person.email && (
                          <p className="text-xs text-gray-500">{person.email}</p>
                        )}
                      </div>
                      {selectedPerson?.id === person.id && (
                        <Check className="h-4 w-4 text-sca-purple-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}

              {personSearchTerm.length >= 3 && !isSearchingPeople && personResults.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No people found matching "{personSearchTerm}"
                </p>
              )}

              {/* Selected person */}
              {selectedPerson && (
                <div className="bg-sca-purple-50 border border-sca-purple-200 rounded-lg p-4">
                  <p className="text-xs text-sca-purple-600 uppercase mb-1">Selected</p>
                  <p className="text-sm font-medium text-gray-900">{selectedPerson.name}</p>
                  {selectedPerson.email && (
                    <p className="text-xs text-gray-600">{selectedPerson.email}</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedPerson(null);
                    setPersonSearchTerm('');
                    setPersonResults([]);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignAsset}
                  disabled={!selectedPerson || isSaving}
                  className="btn-primary"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Manage Categories</h2>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <p className="text-sm text-gray-500">
                Add, edit, or remove asset categories. Changes will apply to all assets.
              </p>

              {/* Add new category */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="New category name..."
                  className="input flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) {
                      const id = newCategoryName.toLowerCase().replace(/[^a-z0-9]+/g, '_');
                      if (!editingCategories.some(c => c.id === id)) {
                        setEditingCategories([
                          ...editingCategories,
                          { id, name: newCategoryName.trim() },
                        ]);
                        setNewCategoryName('');
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newCategoryName.trim()) {
                      const id = newCategoryName.toLowerCase().replace(/[^a-z0-9]+/g, '_');
                      if (!editingCategories.some(c => c.id === id)) {
                        setEditingCategories([
                          ...editingCategories,
                          { id, name: newCategoryName.trim() },
                        ]);
                        setNewCategoryName('');
                      }
                    }
                  }}
                  disabled={!newCategoryName.trim()}
                  className="btn-primary"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* Categories list */}
              <div className="space-y-2">
                {editingCategories.map((cat, index) => (
                  <div
                    key={cat.id}
                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                  >
                    <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={cat.name}
                      onChange={(e) => {
                        const updated = [...editingCategories];
                        updated[index] = { ...cat, name: e.target.value };
                        setEditingCategories(updated);
                      }}
                      className="flex-1 bg-transparent border-0 text-sm focus:outline-none focus:ring-0"
                    />
                    {cat.isDefault && (
                      <span className="text-xs text-gray-400 px-2">Default</span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCategories(editingCategories.filter((_, i) => i !== index));
                      }}
                      className="text-gray-400 hover:text-red-500"
                      title="Remove category"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {editingCategories.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  No categories. Add one above.
                </p>
              )}
            </div>

            <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={saveCategories}
                disabled={isSavingCategories || editingCategories.length === 0}
                className="btn-primary"
              >
                {isSavingCategories ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
